// Settings → Connected apps: the state of this account's Nexus link.

import React, { useCallback, useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, Clock, Loader2, RefreshCw, Unlink } from "lucide-react";
import { Button } from "../ui/button";
import DetailSheet from "../session/generate/DetailSheet";
import { getNexusLink, syncNexus, unlinkNexus } from "../../services/api";
import { useToast } from "../../contexts/ToastContext";

const STATUS = {
  active: { label: "Linked", icon: CheckCircle2, tone: "text-emerald-500" },
  pending: { label: "Waiting for you to confirm in Nexus", icon: Clock, tone: "text-amber-500" },
  error: { label: "Last sync failed — retrying", icon: AlertCircle, tone: "text-amber-500" },
  unlinked: { label: "Removed in Nexus", icon: Unlink, tone: "text-muted-foreground" },
};

export const nexusStatus = (link) => (link ? STATUS[link.status] || STATUS.error : null);

export const useNexusLink = (enabled = true) => {
  const [state, setState] = useState({ loading: true, link: null, allowed: [] });

  const load = useCallback(async () => {
    try {
      const res = await getNexusLink();
      setState({ loading: false, link: res.link, allowed: res.allowed || [] });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => {
    if (enabled) load();
    else setState({ loading: false, link: null, allowed: [] });
  }, [enabled, load]);

  return { ...state, setLink: (link) => setState((s) => ({ ...s, link })) };
};

const NexusSheet = ({ open, nexus, onClose }) => {
  const [busy, setBusy] = useState(null);
  const { showToast } = useToast();
  const { link, loading, allowed, setLink } = nexus;

  const act = async (kind) => {
    setBusy(kind);
    try {
      const res = kind === "sync" ? await syncNexus() : await unlinkNexus();
      setLink(res.link);
      showToast({ title: kind === "sync" ? "Sent your workouts to Nexus" : "Unlinked from Nexus" });
    } catch (e) {
      showToast({ title: kind === "sync" ? "Couldn't reach Nexus" : "Couldn't unlink", description: e.message });
    } finally {
      setBusy(null);
    }
  };

  const st = nexusStatus(link);
  const Icon = st?.icon;
  return (
    <DetailSheet open={open} onOpenChange={(o) => !o && onClose()} title="Nexus">
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : link ? (
        <div className="grid gap-3 text-sm">
          <div className="flex items-start gap-2 rounded-xl border border-border bg-background p-3">
            <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${st.tone}`} />
            <div className="min-w-0">
              <div className="font-medium">
                {st.label}
                {link.profile_name ? ` · ${link.profile_name}` : ""}
              </div>
              <div className="text-muted-foreground">
                {link.nexus_origin.replace("https://", "")}
                {link.last_synced_at ? ` · last sent ${new Date(link.last_synced_at).toLocaleString()}` : ""}
              </div>
              {link.last_error && link.status !== "pending" ? (
                <div className="mt-1 text-muted-foreground">{link.last_error}</div>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {link.status !== "unlinked" ? (
              <Button size="sm" variant="outline" onClick={() => act("sync")} disabled={!!busy}>
                {busy === "sync" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Send now
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              onClick={() => act("unlink")}
              disabled={!!busy}
              className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <Unlink className="mr-2 h-4 w-4" /> Unlink
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm leading-relaxed">
          Nexus is another app of yours that can receive your workouts. To link it, open{" "}
          <span className="font-semibold">Nexus › Settings › Data sources › Khronos</span> and confirm there
          {allowed.length ? ` (${allowed.map((a) => a.replace("https://", "")).join(", ")})` : ""}.
        </p>
      )}
    </DetailSheet>
  );
};

export default NexusSheet;
